/**
 * Cabecera THCC (sections/thcc-cabecera.liquid y sections/thcc-nav-categorias.liquid).
 *
 * 1. Contador del carrito: escucha StandardEvents.cartLinesUpdate, igual que assets/cart-icon.js,
 *    y consulta el carrito al volver desde el historial.
 * 2. Buscador: refleja el ámbito elegido en la pastilla y, si la sección lo pide, pasa lo tecleado
 *    a la búsqueda predictiva de Horizon (#search-modal). Sin este script el formulario envía igual.
 * 3. Mega menú: coloca los tres paneles (Popover API) bajo la barra de categorías. En navegadores sin
 *    Popover API actúa el polyfill que Horizon ya carga (assets/popover-polyfill.js).
 */

const COUNT_SELECTOR = '[data-thcc-cab-count]';
const CART_SELECTOR = '[data-thcc-cab-carrito]';

/* ---------- 1. Contador del carrito ---------- */

/** @param {unknown} value */
function paintCount(value) {
  const count = typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : 0;

  for (const el of document.querySelectorAll(COUNT_SELECTOR)) {
    el.textContent = count > 99 ? '99+' : String(count);
  }

  for (const trigger of document.querySelectorAll(CART_SELECTOR)) {
    const base = trigger.getAttribute('data-thcc-label') || '';
    if (base) trigger.setAttribute('aria-label', `${base}: ${count}`);
  }
}

async function refreshCount() {
  try {
    // `Theme` es un `const` global de Horizon (snippets/scripts.liquid): no cuelga de `window`.
    // @ts-ignore
    const routes = typeof Theme !== 'undefined' ? Theme.routes : undefined;
    const cartUrl = typeof routes?.cart_url === 'string' ? routes.cart_url : '/cart';
    const response = await fetch(`${cartUrl}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!response.ok) return;
    const cart = await response.json();
    paintCount(cart?.item_count);
  } catch (_) {
    // Sin red: se conserva el valor que pintó Liquid.
  }
}

async function initCartCount() {
  if (!document.querySelector(COUNT_SELECTOR)) return;

  try {
    const { StandardEvents } = await import('@shopify/events');
    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      const promise = /** @type {any} */ (event).promise;
      if (promise && typeof promise.then === 'function') {
        promise
          .then((/** @type {any} */ result) =>
            paintCount(result?.cart?.totalQuantity ?? result?.detail?.itemCount ?? 0)
          )
          .catch(() => refreshCount());
      } else {
        refreshCount();
      }
    });
  } catch (_) {
    // El mapa de importaciones no resolvió: queda el respaldo de pageshow.
  }

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refreshCount();
  });
}

/* ---------- 2. Buscador ---------- */

function initSearch() {
  const form = document.querySelector('[data-thcc-buscar]');
  if (!(form instanceof HTMLFormElement)) return;

  const select = form.querySelector('[data-thcc-ambito]');
  const label = form.querySelector('[data-thcc-ambito-txt]');
  const input = form.querySelector('[data-thcc-q]');

  if (select instanceof HTMLSelectElement && label) {
    const sync = () => {
      label.textContent = select.selectedOptions[0]?.textContent?.trim() || '';
    };
    select.addEventListener('change', sync);
    sync();

    // "Todo" no debe enviar `type=` vacío.
    form.addEventListener('submit', () => {
      if (select.value === '') select.disabled = true;
      window.setTimeout(() => {
        select.disabled = false;
      }, 0);
    });
  }

  if (!(input instanceof HTMLInputElement) || !form.hasAttribute('data-thcc-predictiva')) return;

  input.addEventListener('input', (event) => {
    if (/** @type {InputEvent} */ (event).isComposing) return;
    if (!window.matchMedia('(min-width: 750px)').matches) return;
    // Con un ámbito elegido se respeta el envío normal del formulario.
    if (select instanceof HTMLSelectElement && select.value !== '') return;

    const value = input.value;
    if (value.trim() === '') return;

    const modal = /** @type {any} */ (document.getElementById('search-modal'));
    const modalInput = modal?.querySelector('input[name="q"]');
    if (!modal || typeof modal.showDialog !== 'function' || !(modalInput instanceof HTMLInputElement)) return;

    modal.showDialog();
    modalInput.value = value;
    modalInput.dispatchEvent(new Event('input', { bubbles: true }));
    input.value = '';

    requestAnimationFrame(() => {
      modalInput.focus();
      modalInput.setSelectionRange(value.length, value.length);
    });
  });
}

/* ---------- 3. Mega menú ---------- */

/**
 * Tres popovers (panel grande, Marcas, Por familiaridad), cada uno con su botón (`popovertarget`).
 * El panel grande se centra bajo la barra; los desplegables (`.thcc-mega--menor`) alinean su borde
 * izquierdo con el del botón que los abre y no se salen de la ventana.
 */
function initMega() {
  const bar = document.querySelector('[data-thcc-cat]');
  if (!(bar instanceof HTMLElement)) return;

  const panels = Array.from(bar.querySelectorAll('[data-thcc-mega]')).filter(
    (el) => el instanceof HTMLElement && el.id
  );
  if (panels.length === 0) return;

  /** @param {HTMLElement} panel */
  const triggersOf = (panel) =>
    Array.from(document.querySelectorAll('[data-thcc-mega-btn]')).filter(
      (btn) => btn.getAttribute('popovertarget') === panel.id
    );

  /** @param {HTMLElement} panel */
  const isOpen = (panel) => {
    try {
      return panel.matches(':popover-open');
    } catch (_) {
      // Sin Popover API nativa, el polyfill de Horizon marca el panel con la clase `:popover-open`.
      return panel.classList.contains(':popover-open');
    }
  };

  /** Último botón pulsado de cada panel (para alinear el desplegable con el que lo abrió). */
  const lastTrigger = new WeakMap();
  for (const panel of panels) {
    for (const btn of triggersOf(/** @type {HTMLElement} */ (panel))) {
      btn.addEventListener('click', () => lastTrigger.set(panel, btn), true);
    }
  }

  // En la capa superior el panel se mide contra la ventana, no contra la barra: se le pasan
  // el borde inferior y la posición horizontal (la página puede tener barra de scroll propia).
  /** @param {HTMLElement} panel @param {Event} [event] */
  const place = (panel, event) => {
    const rect = bar.getBoundingClientRect();
    const top = Math.round(rect.bottom + window.scrollY + 8);
    panel.style.setProperty('--thcc-mega-top', `${top}px`);

    if (!panel.classList.contains('thcc-mega--menor')) {
      panel.style.setProperty('--thcc-mega-left', `${Math.round(rect.left + window.scrollX + rect.width / 2)}px`);
      panel.style.setProperty('--thcc-mega-max', `${Math.round(rect.width - 32)}px`);
      return;
    }

    const source = /** @type {any} */ (event)?.source;
    const btn =
      (source instanceof HTMLElement && source.getAttribute('popovertarget') === panel.id && source) ||
      lastTrigger.get(panel) ||
      triggersOf(panel)[0];
    const left = btn ? btn.getBoundingClientRect().left : rect.left;
    panel.style.setProperty('--thcc-mega-left', `${Math.round(left + window.scrollX)}px`);
    panel.style.setProperty('--thcc-mega-rel', `${Math.round(left - rect.left)}px`);
  };

  // Ya abierto el desplegable se conoce su ancho: se recoloca para que no se salga de la ventana.
  /** @param {HTMLElement} panel */
  const clamp = (panel) => {
    if (!panel.classList.contains('thcc-mega--menor')) return;
    const view = document.documentElement.clientWidth;
    const box = panel.getBoundingClientRect();
    const overflow = box.right - (view - 16);
    if (overflow > 0) {
      const left = Math.max(16, box.left - overflow);
      const shift = left - box.left;
      const current = parseFloat(panel.style.getPropertyValue('--thcc-mega-left')) || box.left;
      panel.style.setProperty('--thcc-mega-left', `${Math.round(current + shift)}px`);
      const rel = parseFloat(panel.style.getPropertyValue('--thcc-mega-rel')) || 0;
      panel.style.setProperty('--thcc-mega-rel', `${Math.round(rel + shift)}px`);
    }
  };

  for (const el of panels) {
    const panel = /** @type {HTMLElement} */ (el);

    panel.addEventListener('beforetoggle', (event) => {
      if (/** @type {ToggleEvent} */ (event).newState === 'open') place(panel, event);
    });

    panel.addEventListener('toggle', (event) => {
      const open = /** @type {ToggleEvent} */ (event).newState === 'open';
      if (open) clamp(panel);
      for (const btn of triggersOf(panel)) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  window.addEventListener('resize', () => {
    for (const el of panels) {
      const panel = /** @type {HTMLElement} */ (el);
      if (isOpen(panel)) {
        place(panel);
        clamp(panel);
      }
    }
  });
}

/* ---------- 4. Formato de precio en la búsqueda predictiva ---------- */

/**
 * El diálogo de búsqueda predictiva es de Horizon y pinta los precios con el formato de la tienda
 * ("$ 199.00 MXN"). El resto de la tienda usa "$199" (sin ceros finales ni código de moneda): aquí se
 * normaliza el texto de los precios del diálogo, sin tocar su marcado. Solo se cambian nodos de texto.
 */
const PRICE_PATTERN = /\$\s*(\d[\d,]*)(?:\.(\d{2}))?(?:\s*MXN)?/g;

/** @param {string} text */
function normalizePrices(text) {
  return text.replace(PRICE_PATTERN, (_match, whole, cents) => (cents && cents !== '00' ? `$${whole}.${cents}` : `$${whole}`));
}

/** @param {Element} root */
function paintPrices(root) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  /** @type {Text[]} */
  const nodes = [];
  while (walker.nextNode()) {
    const node = /** @type {Text} */ (walker.currentNode);
    if (node.nodeValue && node.nodeValue.includes('$')) nodes.push(node);
  }
  for (const node of nodes) {
    const next = normalizePrices(node.nodeValue ?? '');
    if (next !== node.nodeValue) node.nodeValue = next;
  }
}

function initPredictivePrices() {
  const modal = document.getElementById('search-modal');
  if (!modal) return;
  let queued = false;
  const observer = new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      paintPrices(modal);
    });
  });
  observer.observe(modal, { childList: true, subtree: true, characterData: true });
  paintPrices(modal);
}

/* Las dos secciones cargan este módulo; el navegador lo ejecuta una sola vez. */
initCartCount();
initSearch();
initMega();
initPredictivePrices();
